/**
 * Crypto-backed randomness. Available identically in the browser and in
 * workerd, so the duel deck is shuffled the same way wherever it is built.
 */

/** Uniform integer in [0, max) with rejection sampling — no modulo bias. */
export function randomInt(max: number): number {
  if (max <= 0) throw new RangeError("max must be positive");
  const limit = Math.floor(0xffffffff / max) * max;
  const buf = new Uint32Array(1);
  let n: number;
  do {
    crypto.getRandomValues(buf);
    n = buf[0]!;
  } while (n >= limit);
  return n % max;
}

/** Fisher-Yates. Returns a new array; the input is untouched. */
export function shuffle<T>(input: readonly T[]): T[] {
  const a = input.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Random string over an alphabet, e.g. a room code. */
export function randomString(length: number, alphabet: string): string {
  let out = "";
  for (let i = 0; i < length; i++) out += alphabet[randomInt(alphabet.length)];
  return out;
}

/**
 * `count` distinct indices drawn uniformly from [0, total).
 * A partial Fisher-Yates: it costs `count` random draws rather than shuffling
 * the whole range, which matters when the range is a thousand cards and the
 * duel needs five of them.
 */
export function sampleIndices(total: number, count: number): number[] {
  const n = Math.max(0, Math.min(count, total));
  const pool = Array.from({ length: total }, (_, i) => i);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const j = i + randomInt(total - i);
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
    out.push(pool[i]!);
  }
  return out;
}

/** URL-safe random token of `bytes` entropy. */
export function randomToken(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return btoa(String.fromCharCode(...buf))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
